const FALLBACK_TIME_ZONES = [
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
] as const

export function getTimeZoneOptions(): string[] {
  const intlWithSupportedValues = Intl as unknown as {
    supportedValuesOf?: (key: 'timeZone') => string[]
  }

  const supported = intlWithSupportedValues.supportedValuesOf?.('timeZone') ?? []
  if (supported.length > 0) {
    return supported
  }

  return [...FALLBACK_TIME_ZONES]
}

export function normalizeTimeZone(timeZone?: string | null): string {
  try {
    if (timeZone) {
      Intl.DateTimeFormat('en-US', { timeZone }).format(new Date())
      return timeZone
    }
  } catch (_error) {
    return 'UTC'
  }
  return 'UTC'
}

export function getTimeZoneParts(date: Date, timeZone: string) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date).reduce<Record<string, number>>((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = Number(part.value)
    }
    return acc
  }, {})
}

export function getTimeZoneOffsetMs(date: Date, timeZone: string): number {
  const parts = getTimeZoneParts(date, timeZone)
  const utcEquivalent = Date.UTC(
    parts.year ?? 0,
    (parts.month ?? 1) - 1,
    parts.day ?? 1,
    parts.hour ?? 0,
    parts.minute ?? 0,
    parts.second ?? 0,
  )
  return utcEquivalent - date.getTime()
}

export function zonedDateTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  timeZone: string,
): Date {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second)
  const initialOffset = getTimeZoneOffsetMs(new Date(utcGuess), timeZone)
  let timestamp = utcGuess - initialOffset
  const recalculatedOffset = getTimeZoneOffsetMs(new Date(timestamp), timeZone)
  if (recalculatedOffset !== initialOffset) {
    timestamp = utcGuess - recalculatedOffset
  }
  return new Date(timestamp)
}

export function combineLocalDateTimeInTimeZone(
  dateValue: string,
  timeValue: string,
  timeZone: string,
): Date | null {
  if (!dateValue || !timeValue) {
    return null
  }

  const [year, month, day] = dateValue.split('-').map(Number)
  const [hour = '0', minute = '0'] = timeValue.split(':')
  if (!year || !month || !day) {
    return null
  }

  return zonedDateTimeToUtc(
    year,
    month,
    day,
    Number(hour),
    Number(minute),
    0,
    normalizeTimeZone(timeZone),
  )
}
